import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { PanelLeft, SquarePen } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { useNewChat } from '@/features/assistant';
import { isActivePath, NAV_GROUPS } from '../router/navigation';
import { useNavItems } from '../router/useNavItems';
import { AccountMenu } from './AccountMenu';
import { BrandMark } from './BrandMark';
import { useSidebar } from './sidebarStore';

/** The folded sidebar: icons only, with the same order, tooltips for names, and a button to unfold it. */
export function NavRail() {
  const { pathname } = useLocation();
  const items = useNavItems();
  const newChat = useNewChat();
  const expand = useSidebar().toggle;
  const groups = NAV_GROUPS.map((group) => items.filter((i) => i.group === group)).filter((g) => g.length > 0);

  return (
    <Stack sx={{ height: '100%', alignItems: 'center', bgcolor: 'surface.sidebar', pt: 1.75 }}>
      <BrandMark />
      <Tooltip title="Expand sidebar" placement="right">
        <IconButton aria-label="Expand sidebar" onClick={expand} sx={{ ...railItemSx(false), mt: 1.5 }}>
          <PanelLeft size={17} strokeWidth={1.75} />
        </IconButton>
      </Tooltip>
      <Tooltip title="New chat" placement="right">
        <IconButton aria-label="New chat" onClick={newChat} sx={railItemSx(false)}>
          <SquarePen size={17} strokeWidth={1.75} />
        </IconButton>
      </Tooltip>
      <Stack component="nav" aria-label="Main" sx={{ flexGrow: 1, overflowY: 'auto', alignItems: 'center', gap: 0.5, py: 1 }}>
        {groups.map((groupItems, gi) => (
          <Stack key={groupItems[0]?.group} sx={{ alignItems: 'center', gap: 0.25 }}>
            {gi > 0 ? <Divider flexItem sx={{ my: 0.75, mx: 1.5 }} /> : null}
            {groupItems.map((item) => {
              const isActive = isActivePath(item.path, pathname);
              const Icon = item.icon;
              return (
                <Tooltip key={item.path} title={item.label} placement="right">
                  <IconButton component={NavLink} to={item.path} aria-label={item.label} aria-current={isActive ? 'page' : undefined} sx={railItemSx(isActive)}>
                    <Icon size={17} strokeWidth={1.75} />
                  </IconButton>
                </Tooltip>
              );
            })}
          </Stack>
        ))}
      </Stack>
      <Box sx={{ py: 1, borderTop: 1, borderColor: 'divider', width: '100%', display: 'grid', placeItems: 'center' }}>
        <AccountMenu compact />
      </Box>
    </Stack>
  );
}

const railItemSx = (isActive: boolean) => ({
  width: 40,
  height: 40,
  borderRadius: 1.5,
  color: isActive ? 'text.primary' : 'text.secondary',
  bgcolor: isActive ? 'surface.sunken' : 'transparent',
  '&:hover': { bgcolor: isActive ? 'surface.sunken' : 'surface.subtle', color: 'text.primary' },
});
